import { Events } from '@/services/Events';
import { t } from '@/translations/i18n';
import { base64ToImage, imageToBase64 } from '@/utils/imageBase64Utils';
import { GRADUATION_NAME } from '@/littleWedding/projectConstantes';
import { openDB } from 'idb';
import type { IImageField } from '@/typescript/interfaces/IImageField';
import type { ITemplate } from '@/typescript/interfaces/ITemplate';

async function getTransaction() {
    const db = await openDB('images', 2, {
        upgrade(newDb) {
            if (newDb.objectStoreNames.contains('images')) {
                newDb.deleteObjectStore('images');
            }
            const store = newDb.createObjectStore('images', { keyPath: ['fileName', 'projectId'] });
            store.createIndex('file', ['fileName', 'projectId']);
        },
    });
    const tx = db.transaction('images', 'readwrite');
    return tx;
}

async function saveImages(template: ITemplate, projectId: string) {
    await Promise.all(
        template.configuration.fields.map(async (field) => {
            if (!('filename' in field) || field.filename === GRADUATION_NAME) {
                return;
            }
            const imgField = field as IImageField;
            if (!imgField.url || !imgField.filename) {
                return;
            }
            const imgBlob = await (await fetch(imgField.url)).blob();
            const file = new File([imgBlob], imgField.filename);
            await saveImageToLocalStorage(file, projectId);
        }),
    );
}

async function saveImageToLocalStorage(file: File, projectId: string) {
    const base64 = await imageToBase64(file);
    const tx = await getTransaction();
    const store = tx.objectStore('images');
    const isFileAlreadyInStore = await store.index('file').get([file.name, projectId]);
    if (!isFileAlreadyInStore) {
        await store.add({ fileName: file.name, projectId, type: file.type, size: file.size, data: base64 });
        tx.commit();
    } else {
        if (isFileAlreadyInStore?.data !== base64) {
            isFileAlreadyInStore.data = base64;
            await store.put(isFileAlreadyInStore);
            tx.commit();
        }
    }
}

async function initImages(template: ITemplate, projectId: string): Promise<ITemplate> {
    const tx = await getTransaction();
    const store = tx.objectStore('images');

    const fields = await Promise.all(
        template.configuration.fields.map(async (field) => {
            if (!('filename' in field) || field.filename === GRADUATION_NAME) {
                return field;
            }

            const imgField = field as IImageField;
            if (!imgField.filename) {
                return field;
            }
            try {
                const file = await store.get([imgField.filename, projectId]);
                if (!file) {
                    throw new Error(t('image_load_error', { name: imgField.filename }));
                }
                const blob = base64ToImage(file);
                imgField.url = URL.createObjectURL(blob);

                // eslint-disable-next-line @typescript-eslint/no-unused-vars
            } catch (e) {
                Events.emit('call-toaster', {
                    title: t('error_title'),
                    body: t('image_load_error', { name: imgField.filename }),
                    type: 'error',
                });
            }

            return imgField;
        }),
    );

    tx.commit();

    return {
        ...template,
        configuration: {
            ...template.configuration,
            fields,
        },
    };
}

async function clearImageStorage() {
    const tx = await getTransaction();
    const store = tx.objectStore('images');
    store.clear();
    tx.commit();
}

export { clearImageStorage, initImages, saveImages };
